/**
 * The organisation's price list (cost round 3; shapes in packages/shared/src/schemas/pricing.ts):
 * the rate each model costs, in US dollars per million tokens, made once from the bundled starting
 * list and then the organisation's to correct; and the subscription seats paid per vendor each
 * month. Administrators only: anyone else gets a 403 and sees nothing here.
 */

import { PRICE_SEED_DATE, seatsMonthlyUsd, vendorOf, type PriceEntry, type PriceEntryInput, type PriceList, type Seat } from '@aioe/shared';
import { dateLong } from '@aioe/shared/wording/dateTime';
import { Persons, Xmark } from '@gravity-ui/icons';
import { Tooltip } from '@heroui/react';
import { useState } from 'react';

import { ApiError, problemText, routes } from '../api/client.ts';
import { useLoad } from '../hooks.ts';
import { useMoney } from '../money.tsx';
import { SubjectName } from './SubjectPicker.tsx';
import { Button, ChoiceSelect, ConfirmDialog, Empty, ErrorNote, inputClass, ListBlock, ListGroup, ListRow } from './ui.tsx';
import { VIEW_TONES } from './ui/Art.tsx';

/** The vendors seats and rates name (vendorOf folds the aliases), in words. */
export const VENDOR_WORDS: Record<string, string> = { anthropic: 'Anthropic', openai: 'OpenAI', google: 'Google', 'github-copilot': 'GitHub Copilot', '*': 'Any provider' };
const vendorWords = (v: string) => VENDOR_WORDS[vendorOf(v)] ?? v;

/** A US-dollar rate as typed: up to four decimals, never fewer than two. */
const usd = (n: number) => `US$${n.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
const day = (iso: string) => dateLong(iso.length === 10 ? `${iso}T00:00:00Z` : iso, { timeZone: iso.length === 10 ? 'UTC' : undefined });

const RATE_FIELDS = [
  { key: 'input', label: 'Input tokens' },
  { key: 'output', label: 'Output tokens' },
  { key: 'cacheRead', label: 'Cache reads' },
  { key: 'cacheWrite', label: 'Cache writes' },
  { key: 'perImage', label: 'Each image' },
  { key: 'perRequest', label: 'Each premium request' },
] as const;
type RateKey = (typeof RATE_FIELDS)[number]['key'];
type Draft = Record<RateKey, string> & { provider: string; model: string; effectiveFrom: string; note: string };

const draftOf = (e?: PriceEntry): Draft => ({
  provider: e?.provider ?? 'anthropic',
  model: e?.model ?? '',
  effectiveFrom: e?.effectiveFrom ?? new Date().toISOString().slice(0, 10),
  note: e?.note ?? '',
  input: e ? String(e.input) : '',
  output: e ? String(e.output) : '',
  cacheRead: e?.cacheRead !== undefined ? String(e.cacheRead) : '',
  cacheWrite: e?.cacheWrite !== undefined ? String(e.cacheWrite) : '',
  perImage: e?.perImage !== undefined ? String(e.perImage) : '',
  perRequest: e?.perRequest !== undefined ? String(e.perRequest) : '',
});

/** The rates a draft holds: blank optional rates left out; undefined when one is not a number. */
function ratesOf(d: Draft): (Pick<PriceEntry, 'input' | 'output'> & Partial<Pick<PriceEntry, 'cacheRead' | 'cacheWrite' | 'perImage' | 'perRequest'>>) | undefined {
  const out: Record<string, number> = {};
  for (const { key } of RATE_FIELDS) {
    const raw = d[key].trim();
    if (!raw) {
      if (key === 'input' || key === 'output') out[key] = 0;
      continue;
    }
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 0) return undefined;
    out[key] = n;
  }
  return out as Pick<PriceEntry, 'input' | 'output'>;
}

const inputOf = ({ updatedAt: _a, updatedBy: _b, ...e }: PriceEntry): PriceEntryInput => e;

/** What a line charges, in US dollars as the list keeps it. */
function rateLine(e: PriceEntry): string {
  const parts: string[] = [];
  if (e.input || e.output || !e.perRequest) parts.push(`${usd(e.input)} in`, `${usd(e.output)} out`);
  if (e.cacheRead !== undefined && e.cacheRead !== e.input) parts.push(`${usd(e.cacheRead)} cache read`);
  if (e.perImage) parts.push(`${usd(e.perImage)} an image`);
  if (e.perRequest) parts.push(`${usd(e.perRequest)} a premium request`);
  return parts.join(' · ');
}

/** The price list and the seats, for the Costs page. Renders nothing for someone who is not an administrator. */
export function Pricing() {
  const list = useLoad(() => routes.pricing().catch((e: unknown) => (e instanceof ApiError && e.status === 403 ? null : Promise.reject(e))), []);
  const [fresh, setFresh] = useState<PriceList>();
  if (list.data === null) return null;
  const data = fresh ?? list.data;
  const saved = (l: PriceList) => setFresh(l);
  return (
    <>
      <PriceListPanel data={data} error={list.error} onSaved={saved} />
      {data && <SeatsPanel data={data} onSaved={saved} />}
    </>
  );
}

function PriceListPanel({ data, error, onSaved }: { data: PriceList | undefined; error?: string; onSaved: (l: PriceList) => void }) {
  const [filter, setFilter] = useState('all');
  const [editing, setEditing] = useState<string>();
  const [adding, setAdding] = useState(false);
  const [all, setAll] = useState(false);
  const entries = data?.entries ?? [];
  const vendors = [...new Set(entries.map((e) => vendorOf(e.provider)))].sort((a, b) => vendorWords(a).localeCompare(vendorWords(b)));
  const options: Record<string, string> = { all: 'Every provider', mine: 'Your rates only', ...Object.fromEntries(vendors.map((v) => [v, vendorWords(v)])) };
  const rows = entries
    .filter((e) => filter === 'all' || (filter === 'mine' ? e.source === 'organisation' : vendorOf(e.provider) === filter))
    .sort((a, b) => Number(b.source === 'organisation') - Number(a.source === 'organisation') || vendorWords(a.provider).localeCompare(vendorWords(b.provider)) || a.model.localeCompare(b.model) || b.effectiveFrom.localeCompare(a.effectiveFrom));
  const shown = all ? rows : rows.slice(0, 15);
  const yours = entries.filter((e) => e.source === 'organisation').length;
  return (
    <ListGroup
      title="Price list"
      count={entries.length || undefined}
      description="What each model costs your organisation, in US dollars per million tokens. Every workbench prices its calls by it, and the platform prices the ledger by it."
      actions={
        <>
          {entries.length > 0 && <ChoiceSelect label="Which rates" value={filter} options={options} onChange={(v) => (setFilter(v), setAll(false))} />}
          <Button size="sm" variant="secondary" isDisabled={!data} onPress={() => (setAdding(true), setEditing(undefined))}>
            Add a rate
          </Button>
        </>
      }
    >
      {error ? (
        <ListBlock>
          <ErrorNote error={error} />
        </ListBlock>
      ) : null}
      {!data && !error ? <ListBlock className="text-sm text-muted">Reading the price list…</ListBlock> : null}
      {data && (
        <ListRow
          title="Where the rates come from"
          description={
            <>
              Made from the starting list of {day(data.seedDate || PRICE_SEED_DATE)} on {day(data.seededAt)}
              {data.updatedAt ? (
                <>
                  ; last changed {day(data.updatedAt)}
                  {data.updatedBy ? (
                    <>
                      {' '}
                      by <SubjectName id={data.updatedBy} fallback={data.updatedBy} />
                    </>
                  ) : null}
                </>
              ) : (
                '; not changed since'
              )}
              . {yours ? `${yours} ${yours === 1 ? 'rate is' : 'rates are'} your own. ` : ''}
              The starting list is the providers' published prices around that date: a starting point to check against your contracts, not a quote.
            </>
          }
          control={<RepricePeriod />}
        />
      )}
      {adding && data && (
        <ListBlock>
          <RateEditor
            title="A negotiated rate"
            onCancel={() => setAdding(false)}
            onSave={async (d, rates) => {
              const entry: PriceEntryInput = { provider: d.provider, model: d.model.trim(), effectiveFrom: d.effectiveFrom, ...rates, source: 'organisation', ...(d.note.trim() ? { note: d.note.trim() } : {}) };
              onSaved(await routes.setPricing({ entries: [...data.entries.map(inputOf), entry], seats: data.seats }));
              setAdding(false);
            }}
            isNew
          />
        </ListBlock>
      )}
      {data && entries.length === 0 ? <ListBlock className="text-sm text-muted">No rates yet. Add the rates your contracts give you; calls with no matching rate keep the figure their machine reported.</ListBlock> : null}
      {shown.map((e) => (
        <ListRow
          key={e.id}
          title={<span className="mono">{e.model}</span>}
          description={
            <>
              <span className="block">
                {vendorWords(e.provider)} · {e.source === 'seed' ? 'Starting list' : 'Your rate'}, from {day(e.effectiveFrom)}
                {e.note ? ` · ${e.note}` : ''}
              </span>
              <span className="block tabular-nums text-foreground">{rateLine(e)}</span>
            </>
          }
          control={
            <Button size="sm" variant="ghost" onPress={() => (setEditing(editing === e.id ? undefined : e.id), setAdding(false))}>
              {editing === e.id ? 'Close' : 'Change'}
            </Button>
          }
          compactControl
        >
          {editing === e.id ? (
            <RateEditor
              entry={e}
              title={e.source === 'seed' ? 'Your rate for this model, in place of the starting list’s' : 'Your rate'}
              onCancel={() => setEditing(undefined)}
              onSave={async (d, rates) => {
                onSaved(await routes.patchPricing(e.id, { ...rates, effectiveFrom: d.effectiveFrom, ...(d.note.trim() !== (e.note ?? '') && d.note.trim() ? { note: d.note.trim() } : {}) }));
                setEditing(undefined);
              }}
            />
          ) : null}
        </ListRow>
      ))}
      {rows.length > 15 && (
        <ListBlock>
          <Button size="sm" variant="ghost" onPress={() => setAll(!all)}>
            {all ? 'Show fewer' : `Show all ${rows.length}`}
          </Button>
        </ListBlock>
      )}
    </ListGroup>
  );
}

const DAY_MS = 86_400_000;
const isoDay = (t: number) => new Date(t).toISOString().slice(0, 10);
/** The longest period one call prices again (the API's REPRICE_MAX_DAYS). */
const REPRICE_MAX_DAYS = 31;

/**
 * "Price the period again": the calls already recorded over a period, priced again with the rates
 * in force on each call's day, and the day totals behind costs and budgets worked out afresh. For a
 * rate changed after the calls it should have priced, or figures kept before a pricing fix.
 */
function RepricePeriod() {
  const [today] = useState(() => isoDay(Date.now()));
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(() => isoDay(Date.now() - 6 * DAY_MS));
  const [to, setTo] = useState(today);
  const [error, setError] = useState<string>();
  const [done, setDone] = useState<{ records: number; repriced: number; days: number; from: string; to: string }>();
  const span = Math.round((Date.parse(to) - Date.parse(from)) / DAY_MS) + 1;
  const problem = !from || !to ? 'Choose the first and last day.' : span < 1 ? 'The last day comes before the first.' : span > REPRICE_MAX_DAYS ? `At most ${REPRICE_MAX_DAYS} days at a time; this is ${span}. Price a longer period a month at a time.` : undefined;
  const run = async () => {
    if (problem) return;
    setError(undefined);
    try {
      const out = await routes.repricePeriod({ since: from, until: isoDay(Date.parse(to) + DAY_MS) });
      setDone({ ...out, from, to });
      setOpen(false);
    } catch (e) {
      setError(problemText(e));
    }
  };
  const plural = (n: number, one: string, many: string) => `${n.toLocaleString('en-AU')} ${n === 1 ? one : many}`;
  return (
    <div className="grid justify-items-start gap-1 sm:justify-items-end">
      <Button size="sm" variant="secondary" onPress={() => (setOpen(true), setError(undefined))}>
        Price the period again
      </Button>
      {done && (
        <p className="max-w-xs text-xs text-muted sm:text-right" role="status">
          {day(done.from)}
          {done.to !== done.from ? ` to ${day(done.to)}` : ''}: {plural(done.records, 'call', 'calls')} looked at, {plural(done.repriced, 'priced afresh', 'priced afresh')}, {plural(done.days, 'day', 'days')} of totals worked out again.
        </p>
      )}
      <ConfirmDialog isOpen={open} title="Price the period again" confirmLabel={problem ? 'Choose the days' : `Price ${plural(span, 'day', 'days')} again`} onCancel={() => setOpen(false)} onConfirm={run}>
        <p>Every call recorded over these days is priced again from its tokens with the rate in force on its day, as new calls are. A provider's own figure and calls on a local model stay as they are, and each machine's own figure is kept beside the new one.</p>
        <p>The day totals behind Costs, budgets and statements are then worked out again from the calls, so figures already seen for these days can change.</p>
        <div className="grid grid-cols-2 gap-3 pt-1">
          <label className="grid gap-1 text-xs">
            First day
            <input className={inputClass} type="date" value={from} max={to || today} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label className="grid gap-1 text-xs">
            Last day
            <input className={inputClass} type="date" value={to} min={from} max={today} onChange={(e) => setTo(e.target.value)} />
          </label>
        </div>
        {problem && <p className="text-xs text-danger">{problem}</p>}
        <ErrorNote error={error} />
      </ConfirmDialog>
    </div>
  );
}

/** One rate's fields, typed in US dollars, with the organisation's currency beside each when it is not US dollars. */
function RateEditor({ entry, title, isNew, onSave, onCancel }: { entry?: PriceEntry; title: string; isNew?: boolean; onSave: (d: Draft, rates: NonNullable<ReturnType<typeof ratesOf>>) => Promise<void>; onCancel: () => void }) {
  const { code, format } = useMoney();
  const [d, setD] = useState<Draft>(() => draftOf(entry));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const rates = ratesOf(d);
  const set = (k: keyof Draft, v: string) => setD({ ...d, [k]: v });
  const ready = rates && (rates.input > 0 || rates.output > 0 || (rates.perRequest ?? 0) > 0 || (rates.perImage ?? 0) > 0) && (!isNew || d.model.trim()) && /^\d{4}-\d{2}-\d{2}$/.test(d.effectiveFrom);
  const save = async () => {
    if (!rates) return;
    setBusy(true);
    setError(undefined);
    try {
      await onSave(d, rates);
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(false);
    }
  };
  const vendors: Record<string, string> = { anthropic: 'Anthropic', openai: 'OpenAI', google: 'Google', 'github-copilot': 'GitHub Copilot', '*': 'Any provider' };
  return (
    <div className="grid gap-3 pt-1">
      <div>
        <p className="text-sm font-medium">{title}</p>
        <p className="text-xs text-muted">US dollars per million tokens; images and premium requests in US dollars each. Leave cache rates empty to charge them at the input rate.</p>
      </div>
      {isNew && (
        <div className="grid gap-3 sm:grid-cols-[auto_minmax(0,1fr)]">
          <div className="grid gap-1 text-xs text-muted">
            Provider
            <ChoiceSelect label="Provider" value={d.provider} options={vendors} onChange={(v) => set('provider', v)} />
          </div>
          <label className="grid gap-1 text-xs text-muted">
            Model
            <input className={`${inputClass} mono`} value={d.model} onChange={(e) => set('model', e.target.value)} placeholder="claude-sonnet-5-5, or a prefix ending in *" />
          </label>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
        {RATE_FIELDS.map(({ key, label }) => {
          const n = Number(d[key]);
          return (
            <label key={key} className="grid content-start gap-1 text-xs text-muted">
              {label}
              <input className={`${inputClass} tabular-nums`} type="number" min="0" step="any" inputMode="decimal" value={d[key]} onChange={(e) => set(key, e.target.value)} placeholder={key === 'cacheRead' || key === 'cacheWrite' ? 'Same as input' : key === 'input' || key === 'output' ? '0' : 'None'} />
              {code !== 'USD' && d[key].trim() && Number.isFinite(n) && n > 0 ? <span className="tabular-nums">{format(n, { digits: n < 1 ? 4 : 2 })}</span> : null}
            </label>
          );
        })}
      </div>
      <div className="grid gap-3 sm:grid-cols-[auto_minmax(0,1fr)]">
        <label className="grid gap-1 text-xs text-muted">
          In force from
          <input className={inputClass} type="date" value={d.effectiveFrom} onChange={(e) => set('effectiveFrom', e.target.value)} />
        </label>
        <label className="grid gap-1 text-xs text-muted">
          Note
          <input className={inputClass} value={d.note} onChange={(e) => set('note', e.target.value)} placeholder="Negotiated, enterprise agreement of July 2026" maxLength={300} />
        </label>
      </div>
      {!rates && <p className="text-xs text-danger">Rates are numbers of US dollars, zero or more.</p>}
      <ErrorNote error={error} />
      <div className="flex gap-2">
        <Button size="sm" variant="primary" isDisabled={!ready || busy} isPending={busy} onPress={() => void save()}>
          {isNew ? 'Add the rate' : 'Save the rate'}
        </Button>
        <Button size="sm" variant="ghost" onPress={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

type SeatDraft = { vendor: string; count: string; monthlyUsd: string; note: string };
const SEAT_VENDORS: Record<string, string> = { anthropic: 'Anthropic', openai: 'OpenAI', google: 'Google', 'github-copilot': 'GitHub Copilot' };

/** Seats per vendor: a fixed monthly cost, shared on the statement by each area's calls on paid seats. */
function SeatsPanel({ data, onSaved }: { data: PriceList; onSaved: (l: PriceList) => void }) {
  const tone = VIEW_TONES.costs!;
  const { format, code } = useMoney();
  const [draft, setDraft] = useState<SeatDraft[]>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const seats = data.seats;
  const total = seatsMonthlyUsd(seats);
  const edit = () => setDraft(seats.map((s) => ({ vendor: vendorOf(s.vendor), count: String(s.count), monthlyUsd: String(s.monthlyUsd), note: s.note ?? '' })));
  const parsed: Seat[] | undefined = draft?.every((s) => Number.isInteger(Number(s.count)) && Number(s.count) >= 0 && s.count.trim() !== '' && Number(s.monthlyUsd) >= 0 && s.monthlyUsd.trim() !== '')
    ? draft.map((s) => ({ vendor: s.vendor, count: Number(s.count), monthlyUsd: Number(s.monthlyUsd), ...(s.note.trim() ? { note: s.note.trim() } : {}) }))
    : undefined;
  const twice = draft ? draft.length !== new Set(draft.map((s) => s.vendor)).size : false;
  const save = async () => {
    if (!parsed) return;
    setBusy(true);
    setError(undefined);
    try {
      onSaved(await routes.setPricing({ entries: data.entries.map(inputOf), seats: parsed }));
      setDraft(undefined);
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(false);
    }
  };
  const put = (i: number, k: keyof SeatDraft, v: string) => setDraft(draft!.map((s, j) => (j === i ? { ...s, [k]: v } : s)));
  const free = Object.keys(SEAT_VENDORS).find((v) => !draft?.some((s) => s.vendor === v));
  return (
    <ListGroup
      title="Subscription seats"
      count={seats.length || undefined}
      description="What paid seats cost each month, per vendor, in US dollars. A fixed cost: the statement shares it across business areas and projects by their calls on those seats."
      actions={
        !draft && (
          <Button size="sm" variant="secondary" onPress={edit}>
            {seats.length ? 'Change seats' : 'Add seats'}
          </Button>
        )
      }
      empty={!draft && seats.length === 0 ? <Empty tone={tone} icon={<Persons />} title="No seats recorded" body="Record the seats you pay for (Claude Team, ChatGPT, GitHub Copilot) so the statement carries their cost beside metered spend." compact /> : undefined}
    >
      {draft ? (
        <>
          {draft.length === 0 && <ListBlock className="text-sm text-muted">No seats. Add a vendor whose seats your organisation pays for.</ListBlock>}
          {draft.map((s, i) => {
            const each = Number(s.monthlyUsd);
            return (
              <ListBlock key={i} className="grid gap-2">
                <div className="flex items-center justify-between gap-2">
                  <ChoiceSelect label="Vendor" value={s.vendor} options={SEAT_VENDORS} onChange={(v) => put(i, 'vendor', v)} />
                  <Tooltip>
                    <Button size="sm" variant="ghost" isIconOnly aria-label="Remove this vendor's seats" onPress={() => setDraft(draft.filter((_, j) => j !== i))}>
                      <Xmark />
                    </Button>
                    <Tooltip.Content>Remove this vendor's seats</Tooltip.Content>
                  </Tooltip>
                </div>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-[8rem_10rem_minmax(0,1fr)]">
                  <label className="grid content-start gap-1 text-xs text-muted">
                    Seats
                    <input className={`${inputClass} tabular-nums`} type="number" min="0" step="1" value={s.count} onChange={(e) => put(i, 'count', e.target.value)} />
                  </label>
                  <label className="grid content-start gap-1 text-xs text-muted">
                    Each a month, US$
                    <input className={`${inputClass} tabular-nums`} type="number" min="0" step="any" value={s.monthlyUsd} onChange={(e) => put(i, 'monthlyUsd', e.target.value)} />
                    {code !== 'USD' && each > 0 ? <span className="tabular-nums">{format(each)}</span> : null}
                  </label>
                  <label className="col-span-2 grid content-start gap-1 text-xs text-muted sm:col-span-1">
                    Plan
                    <input className={inputClass} value={s.note} onChange={(e) => put(i, 'note', e.target.value)} placeholder="Copilot Business" maxLength={200} />
                  </label>
                </div>
              </ListBlock>
            );
          })}
          <ListBlock className="grid gap-3">
            {twice && <p className="m-0 text-xs text-danger">Each vendor once: put all of a vendor's seats on one line.</p>}
            {draft.length > 0 && !parsed && <p className="m-0 text-xs text-danger">Seats are a whole number and the monthly cost a number of US dollars, zero or more.</p>}
            <ErrorNote error={error} />
            <div className="flex flex-wrap items-center gap-2">
              {free && (
                <Button size="sm" variant="secondary" onPress={() => setDraft([...draft, { vendor: free, count: '1', monthlyUsd: '', note: '' }])}>
                  Add a vendor
                </Button>
              )}
              <span className="flex-1" />
              <Button size="sm" variant="ghost" onPress={() => (setDraft(undefined), setError(undefined))}>
                Cancel
              </Button>
              <Button size="sm" variant="primary" isDisabled={!parsed || twice || busy} isPending={busy} onPress={() => void save()}>
                Save seats
              </Button>
            </div>
          </ListBlock>
        </>
      ) : (
        <>
          {seats.map((s) => (
            <ListRow
              key={s.vendor}
              title={vendorWords(s.vendor)}
              description={
                <span className="tabular-nums">
                  {s.count} {s.count === 1 ? 'seat' : 'seats'} at {usd(s.monthlyUsd)} a month{s.note ? ` · ${s.note}` : ''}
                </span>
              }
              value={<span className="font-medium tabular-nums">{format(s.count * s.monthlyUsd)}</span>}
            />
          ))}
          <ListRow title="A month, in all" value={<span className="font-medium tabular-nums">{format(total)}</span>} />
        </>
      )}
    </ListGroup>
  );
}
